/**
 * Booking Query Hooks
 * Queries for fetching available booking slots
 */

import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../useAuth";
import { apiPost } from "../../../lib/api-utils";
import { format, addDays, startOfDay, isBefore } from "date-fns";
import { QUERY_CONFIG, ENDPOINTS } from "../config";
import { queryKeys } from "../query-keys";
import type { AvailableDay, OpenSlotsParams } from "../types";
import { buildQueryParams } from "../utils";

/**
 * Get Available Time Slots
 * Fetches available booking slots for a specific person and date range
 */
export const useOpenSlots = ({
  bookeeEmail,
  selectedWeek,
  meetingDuration,
  enabled = true,
}: OpenSlotsParams & { enabled?: boolean }) => {
  const { getAuthToken } = useAuth();

  return useQuery({
    queryKey: queryKeys.booking.openSlotsWithParams(
      bookeeEmail,
      selectedWeek,
      meetingDuration
    ),
    queryFn: async () => {
      const token = getAuthToken();
      if (!token) throw new Error("No authentication token");

      const tomorrow = addDays(startOfDay(new Date()), 1);
      let startDate = startOfDay(selectedWeek);

      if (isBefore(startDate, tomorrow)) {
        startDate = tomorrow;
      }

      const endDate = addDays(startDate, 7);

      const response = await apiPost(
        ENDPOINTS.booking.openSlots,
        {
          emails: [bookeeEmail],
          startDate: format(startDate, "yyyy-MM-dd"),
          endDate: format(endDate, "yyyy-MM-dd"),
          slotDuration: meetingDuration,
        },
        token
      );

      return response as AvailableDay[];
    },
    enabled:
      enabled && !!getAuthToken() && !!bookeeEmail && !!meetingDuration,
    staleTime: QUERY_CONFIG.booking.staleTime,
    gcTime: QUERY_CONFIG.booking.gcTime,
  });
};
